import FlatPickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-flatpickr';
import useBuildVariantProps from '../../../../modules/theme/useBuildVariantProps';
import { DatePickerBaseProps } from './DatePickerBase.types';
import customTimeSelectorPlugin from './Plugins/customTimeSelector';
import dateTimeSelectorPlugin from './Plugins/dateTimeSelector';
import timeSelectorPlugin from './Plugins/timeSelector';
import {
  applyVariantProps,
  setupActiveSyncObserver,
  applyExternalClasses,
  setupDialogBehavior,
} from './DatePickerBase.utils';

const usePresenter = (props: DatePickerBaseProps) => {
  const {
    value,
    onValueChange,
    disabled,
    required,
    enableRange,
    datePickerRef,
    noCalendar,
    enableTime,
    customTimePickerPlugin,
    customDateTimePickerPlugin,
    useCustomTimeInputForTimePicker,
    handleInputReadOnlyState,
    onClose,
    onOpen,
    onReady,
    renderInput, // Extract to prevent it being in otherProps
    className, // Extract to prevent it being in otherProps
    ...otherProps
  } = props;

  const isTimePickerOnly = noCalendar && enableTime;
  const isDateTimePicker = !noCalendar && enableTime;
  const containerRef = useRef<DatePicker>(null);
  const resolvedRef = datePickerRef ?? containerRef;

  const [isInDialog, setIsInDialog] = useState(false);

  const mode: 'range' | 'single' = enableRange ? 'range' : 'single';

  const onDateChange = useCallback(
    (dates: Date[]) => {
      onValueChange?.(enableRange ? dates : dates[0]);
    },
    [onValueChange, enableRange]
  );

  const onOpenDatePicker: FlatPickr.Options.Hook = useCallback(
    (dates: Date[], dateStr: string, instance: Instance) => {
      onOpen?.(dates, dateStr, instance);
      if (handleInputReadOnlyState) {
        handleInputReadOnlyState(true);
      }
    },
    [onOpen, handleInputReadOnlyState]
  );

  const onCloseDatePicker: FlatPickr.Options.Hook = useCallback(
    (dates: Date[], dateStr: string, instance: Instance) => {
      onClose?.(dates, dateStr, instance);
      if (handleInputReadOnlyState) {
        handleInputReadOnlyState(false);
      }
    },
    [onClose, handleInputReadOnlyState]
  );

  const onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.trim();
    onValueChange?.(inputValue);
  };

  const customPlugin = useMemo(() => {
    // If time picker only, use custom time picker plugin if provided, otherwise use default time picker plugin
    if (isTimePickerOnly) {
      if (customTimePickerPlugin) {
        return [customTimePickerPlugin({ minuteStep: 30 })];
      } else if (useCustomTimeInputForTimePicker) { // to use custom time input same as date picker input
        return [customTimeSelectorPlugin({ minuteStep: 30 })];
      } else {
        return [timeSelectorPlugin({ minuteStep: 30 })];
      }
    } else if (enableTime) {
      // If date time picker, use custom date time picker plugin if provided, otherwise use default date time picker plugin
      if (customDateTimePickerPlugin) {
        return [customDateTimePickerPlugin({ minuteStep: 30 })];
      } else {
        return [dateTimeSelectorPlugin({ minuteStep: 30 })];
      }
    }
    return [];
  }, [isTimePickerOnly, enableTime, useCustomTimeInputForTimePicker]);

  const handleKeyDown = (
    _selectedDates: Date[],
    _dateStr: string,
    instance: Instance,
    event: KeyboardEvent
  ) => {
    if (isDateTimePicker && event.key === "Escape") {
      instance.close(); // Close the calendar
      if (handleInputReadOnlyState) {
        handleInputReadOnlyState(false);
      }
    }
  };

  const calendarVariantProps = useBuildVariantProps('date-picker');
  const dayVariantProps = useBuildVariantProps('date-picker-day');

  // Memoize options object to prevent unnecessary Flatpickr reinitialization
  const flatpickrOptions = useMemo(
    () => ({
      mode,
      enableTime,
      noCalendar,
      inline: props.inline,
      allowInput: props.allowInput,
      altInput: isTimePickerOnly,
      altInputClass: 'custom-time-selector-input',
      plugins: customPlugin,
      onKeyDown: handleKeyDown,
      static: !!isInDialog,
      ...otherProps,
    }),
    [mode, enableTime, noCalendar, props.inline, props.allowInput, isTimePickerOnly, customPlugin, handleKeyDown, isInDialog, otherProps]
  );

  useLayoutEffect(() => {
    if (resolvedRef?.current?.flatpickr?.input) {
      const inputEl = resolvedRef.current.flatpickr.input as HTMLElement;
      const possibleDialog = inputEl.closest('[role="dialog"]');
      if (possibleDialog instanceof HTMLElement) {
        setIsInDialog(true);
      } else {
        setIsInDialog(false);
      }
    }
  }, [resolvedRef]);

  // Store cleanup functions for day elements
  const dayCleanupFns = useRef<Map<HTMLElement, () => void>>(new Map());

  const handleDayCreate = useCallback(
    (_: Date[], __: string, ___: Instance, dayElem: HTMLElement) => {
      // Apply variant props
      applyVariantProps(dayElem, dayVariantProps);

      // Setup active sync observer and store cleanup function
      const cleanup = setupActiveSyncObserver(dayElem);
      dayCleanupFns.current.set(dayElem, cleanup);
    },
    [dayVariantProps]
  );

  // Store cleanup function for dialog behavior
  const dialogCleanupFn = useRef<(() => void) | null>(null);

  const handleCalendarReady = useCallback(
    (dates: Date[], dateStr: string, calendarInstance: Instance) => {
      if (calendarInstance.calendarContainer) {
        // Apply external classes
        applyExternalClasses(calendarInstance.calendarContainer, className);

        // Apply variant props
        applyVariantProps(calendarInstance.calendarContainer, calendarVariantProps);

        // Setup dialog behavior if needed
        if (isInDialog) {
          // Clean up previous dialog behavior if it exists
          if (dialogCleanupFn.current) {
            dialogCleanupFn.current();
          }
          dialogCleanupFn.current = setupDialogBehavior(calendarInstance);
        }
      }

      // Call user's onReady callback
      onReady?.(dates, dateStr, calendarInstance);
    },
    [className, calendarVariantProps, isInDialog, onReady]
  );

  // Cleanup on unmount
  useLayoutEffect(() => {
    return () => {
      // Cleanup all day observers
      dayCleanupFns.current.forEach((cleanup) => cleanup());
      dayCleanupFns.current.clear();

      // Cleanup dialog behavior
      if (dialogCleanupFn.current) {
        dialogCleanupFn.current();
        dialogCleanupFn.current = null;
      }
    };
  }, []);

  return {
    value,
    datePickerRef: resolvedRef,
    disabled,
    required,
    onDateChange,
    onCloseDatePicker,
    onOpenDatePicker,
    handleDayCreate,
    handleCalendarReady,
    flatpickrOptions,
  };
};

export default usePresenter;